import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import "../materials/materialRecord.css";
import "./planning.css";
import { PlanningCalendar } from "./PlanningCalendar";
import { PlanningDesktop } from "./PlanningDesktop";
import { TaskSpanBar } from "./TaskSpanBar";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { buildCalendarEvents } from "../../helpers/planning/calendarModel";
import { nonWorkingDay, SCHEDULE_UNAVAILABLE_NOTE, spansNeedSchedule } from "../../helpers/planning/timelineLayout";
import { usePermissions } from "../../hooks/usePermissions";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import { safeUserErrorMessage } from "../../helpers/safeErrors";
import { formatTargetCompletion } from "../../helpers/projectStructure";
import { loadPlanningData } from "../../helpers/planning/planningData";
import {
  PLAN_KIND,
  buildPlanningTree,
  collectUnscheduled,
  computeDateRange,
  dayIndexToISO,
  describeNode,
  flattenPlanning,
  focusTargetDay,
  localTodayISO,
  planningBucket,
  pruneByPredicate,
  scopePlanning,
  summarizePlanning,
  weekdayOf,
} from "../../helpers/planning/planningModel";

/** Width of one day column on the shared time plane (px). */
export const PLANNING_DAY_W = 32;
const WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

function monthLabel(index) {
  return new Date(index * 86400000).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function rangeLabel(startIdx, endIdx) {
  const fmt = (i) =>
    new Date(i * 86400000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(startIdx)} – ${fmt(endIdx)}`;
}

/** Compact marker date: "Oct 12", with the year only when it is not this year. */
function markerDate(iso, todayISO) {
  const full = formatTargetCompletion(iso);
  if (!full) return "";
  return iso.slice(0, 4) === todayISO.slice(0, 4) ? full.replace(/,\s*\d{4}$/, "") : full;
}

const KIND_LABEL = {
  [PLAN_KIND.PROJECT]: "Project",
  [PLAN_KIND.LOCATION]: "Location",
  [PLAN_KIND.FEATURE]: "Feature",
  [PLAN_KIND.TASK]: "Task",
};

/**
 * Planning V1: a hierarchical timeline over the existing Project hierarchy.
 * Left: sticky hierarchy labels. Right: one shared, horizontally scrolling
 * date plane with a TODAY line. Dates are honest markers (no durations).
 */
export function PlanningScreen({ onBack, now, onOpenItem, savedState = null, onSaveState }) {
  const { actor } = usePermissions();
  const workSchedule = useWorkSchedule();
  const isDesktop = useIsDesktopViewport();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set(savedState?.expanded || []));
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [projectFilter, setProjectFilterState] = useState(savedState?.projectFilter || ""); // "" = All Jobs
  const [locationFilter, setLocationFilterState] = useState(savedState?.locationFilter || "");
  const [featureFilter, setFeatureFilter] = useState(savedState?.featureFilter || "");
  // Changing an upstream level clears the levels below it.
  const setProjectFilter = useCallback((value) => {
    setProjectFilterState(value);
    setLocationFilterState("");
    setFeatureFilter("");
  }, []);
  const setLocationFilter = useCallback((value) => {
    setLocationFilterState(value);
    setFeatureFilter("");
  }, []);
  // Calendar is the normal entry on every device. A snapshot from an opened
  // item, or any other caller that names timeline or calendar, keeps that view.
  const [view, setView] = useState(() => {
    const requested = savedState?.view;
    return requested === "timeline" || requested === "calendar" ? requested : "calendar";
  });
  const [scrollLeft, setScrollLeft] = useState(savedState?.scrollLeft || 0);
  const restoreScroll = useRef(savedState?.view === "timeline" ? savedState.scrollLeft ?? null : null);
  const [calSel, setCalSel] = useState(savedState?.calendar || null);
  const [viewWidth, setViewWidth] = useState(0);
  const [focus, setFocus] = useState(savedState?.focus || null); // KPI focus: overdue | week | upcoming | none
  const openItem = onOpenItem
    ? (target) => onOpenItem(target, data?.projects?.find((p) => p.project_id === target.projectId))
    : undefined;
  const scrollerRef = useRef(null);
  const cornerRef = useRef(null);
  const didInitialScroll = useRef(false);

  const today = useMemo(() => localTodayISO(now || new Date()), [now]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    loadPlanningData(actor)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(safeUserErrorMessage(err, "Unable to load planning."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actor]);

  const tree = useMemo(
    () => (data ? buildPlanningTree({ ...data, today, workSchedule }) : []),
    [data, today, workSchedule],
  );
  // The ONE Planning-level filter: Project -> Location -> Feature (each level
  // offered only once its parent is chosen; stale / inaccessible ids resolve
  // to ""). Everything below (Timeline rows, Calendar, summary, No Target
  // Date) derives from `visibleTree`; the date window stays computed from the
  // full tree so choosing a filter never moves the Timeline.
  const scope = useMemo(
    () => scopePlanning(tree, { projectId: projectFilter, locationId: locationFilter, featureId: featureFilter }),
    [tree, projectFilter, locationFilter, featureFilter],
  );
  const jobOptions = scope.projectOptions;
  const activeFilter = scope.selection.projectId;
  const visibleTree = scope.tree;
  const needsSchedule = useMemo(() => spansNeedSchedule(visibleTree), [visibleTree]);
  const range = useMemo(() => computeDateRange(tree, today), [tree, today]);
  const summary = useMemo(() => summarizePlanning(visibleTree, today), [visibleTree, today]);
  // KPI focus (same classification as Desktop Planning); Calendar stays unfocused.
  const focusedTree = useMemo(
    () => (focus ? pruneByPredicate(visibleTree, (node) => planningBucket(node, today) === focus) : visibleTree),
    [visibleTree, focus, today],
  );
  const unscheduled = useMemo(() => collectUnscheduled(focusedTree), [focusedTree]);
  const rows = useMemo(() => flattenPlanning(focusedTree, expanded), [focusedTree, expanded]);
  const calendarEvents = useMemo(() => buildCalendarEvents(visibleTree), [visibleTree]);

  const scrollToToday = useCallback(
    (smooth) => {
      const el = scrollerRef.current;
      if (!el) return;
      const left = Math.max(0, (range.todayIdx - range.start - 1) * PLANNING_DAY_W);
      if (smooth && typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
      else el.scrollLeft = left;
    },
    [range.start, range.todayIdx],
  );

  // Open on a window around Today (once, after the data renders).
  useLayoutEffect(() => {
    if (isDesktop) return; // the desktop workspace manages its own scroller
    if (view !== "timeline") {
      // The Timeline unmounts while Calendar is shown; re-arm so it reopens on Today.
      didInitialScroll.current = false;
      return;
    }
    if (loading || !data || didInitialScroll.current) return;
    didInitialScroll.current = true;
    if (restoreScroll.current != null && scrollerRef.current) {
      scrollerRef.current.scrollLeft = restoreScroll.current; // coming back from an opened item
      restoreScroll.current = null;
      return;
    }
    scrollToToday(false);
  }, [loading, data, scrollToToday, view, isDesktop]);

  // Keep a snapshot of the user's place so an item opened from here can return to it.
  useEffect(() => {
    onSaveState?.({ view, projectFilter, locationFilter, featureFilter, focus, expanded: [...expanded], calendar: calSel, scrollLeft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, projectFilter, locationFilter, featureFilter, focus, expanded, calSel, scrollLeft]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setViewWidth(el.clientWidth - (cornerRef.current?.offsetWidth || 0));
    measure();
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading, data, view]);

  const handleScroll = useCallback((event) => {
    setScrollLeft(event.currentTarget.scrollLeft);
  }, []);

  /** Put a date at (or one day before) the left edge of the timeline. */
  function scrollToDay(idx, lead = 1) {
    const el = scrollerRef.current;
    if (!el || idx == null) return;
    const left = Math.max(0, (idx - range.start - lead) * PLANNING_DAY_W);
    if (typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
    else el.scrollLeft = left;
  }

  // A KPI tap sets the focus AND the initial window; afterwards the user
  // navigates freely (nothing re-applies the target on later renders).
  function chooseFocus(next) {
    if (focus === next) {
      setFocus(null);
      if (view === "timeline") scrollToToday(true);
      return;
    }
    setFocus(next);
    if (next === "none") setShowUnscheduled(true); // expose the existing No Target Date list
    if (view !== "timeline") return;
    const target = focusTargetDay(visibleTree, next, today);
    if (target != null) scrollToDay(target, next === "week" ? 0 : 1);
  }

  function nudge(days) {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft + days * PLANNING_DAY_W;
    if (typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
    else el.scrollLeft = left;
  }

  function toggle(node) {
    const key = `${node.kind}:${node.id}`;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (isDesktop) {
    return (
      <PlanningDesktop
        onBack={onBack}
        today={today}
        data={data}
        loading={loading}
        error={error}
        tree={tree}
        visibleTree={visibleTree}
        jobOptions={jobOptions}
        activeFilter={activeFilter}
        setProjectFilter={setProjectFilter}
        locationOptions={scope.locationOptions}
        activeLocation={scope.selection.locationId}
        setLocationFilter={setLocationFilter}
        featureOptions={scope.featureOptions}
        activeFeature={scope.selection.featureId}
        setFeatureFilter={setFeatureFilter}
        workSchedule={workSchedule}
        needsSchedule={needsSchedule}
        summary={summary}
        range={range}
        expanded={expanded}
        toggle={toggle}
        onOpenItem={openItem}
        view={view}
        onViewChange={setView}
        calendarSelection={calSel}
        onCalendarSelectionChange={setCalSel}
      />
    );
  }

  const trackWidth = range.days * PLANNING_DAY_W;
  const firstVisible = range.start + Math.max(0, Math.floor(scrollLeft / PLANNING_DAY_W));
  const visibleDays = Math.max(1, Math.floor(Math.max(0, viewWidth) / PLANNING_DAY_W));
  const lastVisible = Math.min(range.end, firstVisible + visibleDays - 1);

  const dayCells = [];
  for (let i = 0; i < range.days; i += 1) {
    const idx = range.start + i;
    const iso = dayIndexToISO(idx);
    const dom = Number(iso.slice(8, 10));
    const wd = weekdayOf(idx);
    dayCells.push(
      <div
        key={idx}
        className={`pl-day${nonWorkingDay(idx, workSchedule) ? " pl-day--weekend" : ""}${
          idx === range.todayIdx ? " pl-day--today" : ""
        }`}
        data-date={iso}
      >
        {dom === 1 || i === 0 ? <span className="pl-day__month">{monthLabel(idx)}</span> : null}
        <span className="pl-day__num">{dom}</span>
        <span className="pl-day__wd">{WEEKDAY_LETTERS[wd]}</span>
      </div>,
    );
  }

  const weekendStripes = [];
  for (let i = 0; i < range.days; i += 1) {
    if (nonWorkingDay(range.start + i, workSchedule)) {
      weekendStripes.push(
        <span key={i} className="pl-weekend" style={{ left: i * PLANNING_DAY_W, width: PLANNING_DAY_W }} />,
      );
    }
  }
  const todayLeft = (range.todayIdx - range.start) * PLANNING_DAY_W + PLANNING_DAY_W / 2;

  return (
    <div className="screen nx-screen rec-page pl-page" data-testid="planning-screen">
      <RecBar title="Planning" leading="back" onBack={onBack} />
      <div className="pl-subtitle">See what’s coming up across your projects.</div>

      {error ? <div className="est-inline-error">{error}</div> : null}
      {loading ? <div className="pl-empty">Loading planning…</div> : null}

      {!loading && data ? (
        <>
          {data.fromDevice ? (
            <div className="pl-banner" role="status">
              Showing what’s saved on this device. Reconnect for the latest dates.
            </div>
          ) : null}

          <div className="pl-summary" aria-label="Planning summary">
            {[
              ["overdue", "pl-stat--overdue", summary.overdue, "Overdue"],
              ["week", "pl-stat--week", summary.thisWeek, "This Week"],
              ["upcoming", "", summary.upcoming, "Upcoming"],
              ["none", "pl-stat--muted", summary.noTarget, "No Target Date"],
            ].map(([key, tone, value, label]) => (
              <button
                key={key}
                type="button"
                className={`pl-stat ${tone}${focus === key ? " is-on" : ""}`.replace(/\s+/g, " ").trim()}
                aria-pressed={focus === key}
                onClick={() => chooseFocus(key)}
                title={focus === key ? "Clear focus" : `Focus the board on ${label.toLowerCase()} work`}
              >
                <strong>{value}</strong>
                <span>{label}</span>
              </button>
            ))}
          </div>

          {tree.length === 0 ? (
            <div className="pl-empty">No active projects to plan yet.</div>
          ) : (
            <>
              <div className="pl-filters" data-testid="planning-filters">
                <label className="pl-jobfilter rec-select">
                  <span className="pl-sr">Job</span>
                  <select
                    className="rec-input"
                    aria-label="Job"
                    value={activeFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                  >
                    <option value="">All Jobs</option>
                    {jobOptions.map((job) => (
                      <option key={job.id} value={job.id}>
                        {job.name}
                      </option>
                    ))}
                  </select>
                </label>
                {activeFilter && scope.locationOptions.length ? (
                  <label className="pl-jobfilter rec-select">
                    <span className="pl-sr">Location</span>
                    <select
                      className="rec-input"
                      aria-label="Location"
                      value={scope.selection.locationId}
                      onChange={(e) => setLocationFilter(e.target.value)}
                    >
                      <option value="">All Locations</option>
                      {scope.locationOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {scope.selection.locationId && scope.featureOptions.length ? (
                  <label className="pl-jobfilter rec-select">
                    <span className="pl-sr">Feature</span>
                    <select
                      className="rec-input"
                      aria-label="Feature"
                      value={scope.selection.featureId}
                      onChange={(e) => setFeatureFilter(e.target.value)}
                    >
                      <option value="">All Features</option>
                      {scope.featureOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <div className="pl-viewtoggle" role="group" aria-label="Planning view">
                {[
                  ["timeline", "Timeline"],
                  ["calendar", "Calendar"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`pl-viewtoggle__btn${view === id ? " is-active" : ""}`}
                    aria-pressed={view === id}
                    onClick={() => setView(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {view === "calendar" ? (
                <PlanningCalendar
                  events={calendarEvents}
                  today={today}
                  minutesPerDay={workSchedule.minutesPerDay}
                  onOpenEvent={openItem}
                  initialSelected={calSel?.selected}
                  initialMonth={calSel?.monthStart}
                  onSelectionChange={setCalSel}
                />
              ) : null}

              {view === "timeline" ? (
              <>
              <div className="pl-toolbar">
                <button type="button" className="pl-iconbtn" aria-label="Earlier" onClick={() => nudge(-7)}>
                  <RecIcon name="back" />
                </button>
                <div className="pl-range" data-testid="planning-range">
                  {rangeLabel(firstVisible, lastVisible)}
                </div>
                <button type="button" className="pl-iconbtn" aria-label="Later" onClick={() => nudge(7)}>
                  <RecIcon name="chevron" />
                </button>
                <button type="button" className="pl-today-btn" onClick={() => scrollToToday(true)}>
                  Today
                </button>
              </div>

              {needsSchedule ? (
                <div className="pl-banner" role="status" data-testid="planning-schedule-unavailable">
                  {SCHEDULE_UNAVAILABLE_NOTE}
                </div>
              ) : null}
              <div
                className="pl-panel"
                ref={scrollerRef}
                onScroll={handleScroll}
                data-testid="planning-timeline"
              >
                <div className="pl-inner" style={{ "--pl-track": `${trackWidth}px` }}>
                  <div className="pl-head">
                    <div className="pl-corner" ref={cornerRef}>{monthLabel(firstVisible)}</div>
                    <div className="pl-days" style={{ width: trackWidth }}>
                      {dayCells}
                      <span className="pl-today-tag" style={{ left: todayLeft }}>
                        TODAY
                      </span>
                    </div>
                  </div>

                  <div className="pl-rows">
                    <div className="pl-plane" aria-hidden="true" style={{ width: trackWidth }}>
                      {weekendStripes}
                      <span
                        className="pl-today-line"
                        data-testid="planning-today-line"
                        style={{ left: todayLeft }}
                      />
                    </div>
                    {rows.map(({ node, depth, expandable, isOpen }) => {
                      const dated = node.dayIndex != null;
                      const left = dated ? (node.dayIndex - range.start) * PLANNING_DAY_W + PLANNING_DAY_W / 2 : 0;
                      const labelFlip = dated && left > trackWidth - 76;
                      const sub =
                        node.kind === PLAN_KIND.PROJECT || node.kind === PLAN_KIND.LOCATION
                          ? describeNode(node)
                          : "";
                      const overdue = Boolean(node.overdue);
                      const labelBody = (
                        <>
                          {expandable ? (
                            <RecIcon name="chevron" className={`pl-chev${isOpen ? " is-open" : ""}`} />
                          ) : (
                            <span className="pl-chev-spacer" aria-hidden="true" />
                          )}
                          <span className="pl-label__text">
                            <span className={`pl-label__name${node.completed ? " is-done" : ""}`}>
                              {node.name}
                            </span>
                            {sub ? <span className="pl-label__sub">{sub}</span> : null}
                          </span>
                        </>
                      );
                      return (
                        <div
                          key={`${node.kind}:${node.id}`}
                          className={`pl-row pl-row--${node.kind}`}
                          data-kind={node.kind}
                          data-depth={depth}
                          style={{ "--pl-depth": depth }}
                        >
                          {expandable ? (
                            <button
                              type="button"
                              className="pl-label pl-label--btn"
                              aria-expanded={isOpen}
                              aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.name}`}
                              onClick={() => toggle(node)}
                            >
                              {labelBody}
                            </button>
                          ) : (
                            <div className="pl-label">{labelBody}</div>
                          )}
                          <div className="pl-track" style={{ width: trackWidth }}>
                            {node.kind === PLAN_KIND.TASK ? (
                              <TaskSpanBar node={node} startIdx={range.start} endIdx={range.end} dayW={PLANNING_DAY_W} />
                            ) : null}
                            {dated ? (
                              <span
                                className={`pl-marker pl-marker--${node.kind}${overdue ? " is-overdue" : ""}${
                                  node.completed ? " is-done" : ""
                                }${labelFlip ? " is-flipped" : ""}`}
                                style={{ left }}
                                data-testid={`marker-${node.kind}`}
                                data-date={node.date}
                                title={`${KIND_LABEL[node.kind]} · ${formatTargetCompletion(node.date)}`}
                              >
                                <i className="pl-marker__shape" />
                                <span className="pl-marker__label">{markerDate(node.date, today)}</span>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <ul className="pl-legend" aria-label="Legend" data-testid="planning-legend">
                <li><i className="tk-legend-span" />Work: start → expected completion</li>
                <li><i className="tk-legend-due" />Due date (deadline)</li>
              </ul>

              </>
              ) : null}

              <div className="pl-unsched">
                <button
                  type="button"
                  className="pl-unsched__head"
                  aria-expanded={showUnscheduled}
                  onClick={() => setShowUnscheduled((v) => !v)}
                >
                  <RecIcon name="doc" className="pl-unsched__icon" />
                  <span className="pl-unsched__text">
                    <strong>No Target Date</strong>
                    <span>
                      {unscheduled.total === 0
                        ? "Everything has a date"
                        : `${unscheduled.total} item${unscheduled.total === 1 ? "" : "s"} (${[
                            unscheduled.counts.project && `${unscheduled.counts.project} project${unscheduled.counts.project === 1 ? "" : "s"}`,
                            unscheduled.counts.location && `${unscheduled.counts.location} location${unscheduled.counts.location === 1 ? "" : "s"}`,
                            unscheduled.counts.feature && `${unscheduled.counts.feature} feature${unscheduled.counts.feature === 1 ? "" : "s"}`,
                            unscheduled.counts.task && `${unscheduled.counts.task} task${unscheduled.counts.task === 1 ? "" : "s"}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")})`}
                    </span>
                  </span>
                  <RecIcon name="chevron" className={`pl-chev${showUnscheduled ? " is-open" : ""}`} />
                </button>
                {showUnscheduled && unscheduled.total > 0 ? (
                  <ul className="pl-unsched__list">
                    {unscheduled.items.map(({ node, projectName, target }) => {
                      const inner = (
                        <>
                          <span className="pl-unsched__kind">{KIND_LABEL[node.kind]}</span>
                          <span className="pl-unsched__name">{node.name}</span>
                          {projectName ? <span className="pl-unsched__proj">{projectName}</span> : null}
                        </>
                      );
                      return (
                        <li key={`${node.kind}:${node.id}`} className={openItem ? "is-openable" : undefined}>
                          {openItem ? (
                            <button
                              type="button"
                              className="pl-unsched__btn"
                              aria-label={`Open ${KIND_LABEL[node.kind].toLowerCase()}: ${node.name}`}
                              onClick={() => openItem(target)}
                            >
                              {inner}
                            </button>
                          ) : (
                            inner
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
